import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';
import definition from '@job-schemas/support/autopay-card-detach-failed.json';
import { jobSchema } from '../../job-schema.js';
import { cardDetachFailed } from './autopay-card-detach-failed-logic.js';

/**
 * The nodeStream SUPPORT card raised when a company changed its automatic payments card but the old card couldn't be
 * removed at Stripe (#25 phase 6, G13). The change stands: the new card is live. Support removes the old card from the
 * company's Stripe customer by hand (the card links to it in the Stripe dashboard) and marks it resolved. One per company
 * and old card.
 */
@customElement(NSSupportAutopayCardDetachFailed.is)
export default class NSSupportAutopayCardDetachFailed extends NSJobComponent {
  static get is() {
    return 'ns-support-autopay-card-detach-failed';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { resolved: { type: 'hidden' } }, icon: 'bank-card' });
  }

  static styles = [nsLayoutStyles, ...super.styles];

  @state()
  private __busy = false;

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __resolved() {
    if (this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await this.outcome('resolved');
    } catch (err) {
      this.showError(err, 'Unable to mark this resolved. Please try again.');
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const card = cardDetachFailed(this._job?.metadata);
    return html`
      <ns-job-layout>
        <p slot="intro">
          ${card.stopped
            ? `${card.companyName} stopped its automatic payments, but its card couldn't be removed at Stripe.`
            : `${card.companyName} changed its automatic payments card, but its old card couldn't be removed at Stripe.`}
        </p>
        <div class="job-panel">
          <h3>The old card</h3>
          <ns-detail-list
            .rows=${[
              { label: 'Company', value: card.companyName },
              { label: 'Company ID', value: card.companyId },
              { label: 'Old card', value: card.oldCard },
              { label: 'Payment method', value: card.oldPaymentMethodId },
              {
                label: 'Stripe customer',
                value: card.stripeCustomerUrl
                  ? html`
                      <a href=${card.stripeCustomerUrl} target="_blank" rel="noopener noreferrer">
                        ${card.stripeCustomerId ?? 'Open in Stripe'}
                      </a>
                    `
                  : card.stripeCustomerId,
              },
              ...(card.stopped ? [] : [{ label: 'New card, now live', value: card.newCard }]),
              { label: 'Failed', value: card.failedAt },
              { label: 'Error', value: card.error },
            ]}
          ></ns-detail-list>
          <p>
            ${card.stopped
              ? "Open the customer in the Stripe dashboard and remove the card's payment method. The company has no automatic payments card now. Mark this resolved once the card is removed."
              : "Open the customer in the Stripe dashboard and remove the old card's payment method. The new card stays as the company's automatic payments card. Mark this resolved once the old card is removed."}
          </p>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>Why this matters</h3>
          <p>
            ${card.stopped
              ? 'nodeStream never charges this card again: the signatories stopped automatic payments on the public ledger. Removing it at Stripe leaves no card saved for them.'
              : "nodeStream never charges the old card again: automatic payments use the new one. Removing it at Stripe keeps only the card the company's signatories approved on the customer."}
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__resolved()}>
        Resolved
      </wa-button>
    `;
  }
}
